import type {
  BetMove,
  CoinDenomination,
  CoinInventory,
  ShowMeTheCoinPlayerId,
  ShowMeTheCoinState,
  ShowMeTheCoinViewerState,
} from "../types";
import {
  COIN_DENOMINATIONS,
  LEAD_MAX_SUBMIT,
  LEAD_MIN_SUBMIT,
  SHOW_ME_THE_COIN_BET_UNIT,
  SHOW_ME_THE_COIN_PLAYERS,
  SHOW_ME_THE_COIN_RAISE_CAP,
} from "../types";

export function isPlayerId(value: unknown): value is ShowMeTheCoinPlayerId {
  return (
    typeof value === "string" &&
    SHOW_ME_THE_COIN_PLAYERS.includes(value as ShowMeTheCoinPlayerId)
  );
}

export function getOpponentPlayerId(
  playerId: ShowMeTheCoinPlayerId,
): ShowMeTheCoinPlayerId {
  return playerId === "p1" ? "p2" : "p1";
}

export function isCoinDenomination(value: number): value is CoinDenomination {
  return (COIN_DENOMINATIONS as readonly number[]).includes(value);
}

/** Total face value of a list of coin denominations. */
export function coinValue(coins: readonly number[]): number {
  return coins.reduce((sum, coin) => sum + coin, 0);
}

/** Total number of coins remaining in an inventory. */
export function inventoryCount(inventory: CoinInventory): number {
  return COIN_DENOMINATIONS.reduce((sum, denom) => sum + inventory[denom], 0);
}

/** Total face value remaining in an inventory. */
export function inventoryValue(inventory: CoinInventory): number {
  return COIN_DENOMINATIONS.reduce(
    (sum, denom) => sum + denom * inventory[denom],
    0,
  );
}

/** Tally a coin list into per-denomination counts. */
export function tallyCoins(
  coins: readonly number[],
): Record<CoinDenomination, number> {
  const tally: Record<CoinDenomination, number> = {
    500: 0,
    100: 0,
    50: 0,
    10: 0,
  };
  for (const coin of coins) {
    if (isCoinDenomination(coin)) tally[coin] += 1;
  }
  return tally;
}

/** Whether the inventory holds at least the submitted coins. */
export function hasCoins(
  inventory: CoinInventory,
  coins: readonly number[],
): boolean {
  const tally = tallyCoins(coins);
  return COIN_DENOMINATIONS.every((denom) => inventory[denom] >= tally[denom]);
}

/** Remove submitted coins from an inventory, returning a new inventory. */
export function removeCoins(
  inventory: CoinInventory,
  coins: readonly number[],
): CoinInventory {
  const tally = tallyCoins(coins);
  return {
    500: inventory[500] - tally[500],
    100: inventory[100] - tally[100],
    50: inventory[50] - tally[50],
    10: inventory[10] - tally[10],
  };
}

/** Legal submit count range for the follower given the lead's count. */
export function followerCountRange(leadCount: number): {
  readonly min: number;
  readonly max: number;
} {
  return { min: Math.max(1, leadCount - 1), max: leadCount + 1 };
}

export function isLeadCountValid(count: number): boolean {
  return count >= LEAD_MIN_SUBMIT && count <= LEAD_MAX_SUBMIT;
}

/** Chips the acting player must add to match the opponent (0 if none). */
export function outstandingToCall(state: ShowMeTheCoinState): number {
  const opponent = getOpponentPlayerId(state.currentPlayerId);
  return Math.max(
    0,
    state.contributed[opponent] - state.contributed[state.currentPlayerId],
  );
}

/** Legal betting moves for the current actor. */
export function legalBetMoves(state: ShowMeTheCoinState): readonly BetMove[] {
  if (state.phase !== "bet") return [];
  const actor = state.currentPlayerId;
  const chips = state.chips[actor];
  const toCall = outstandingToCall(state);

  if (toCall > 0) {
    const moves: BetMove[] = ["fold"];
    if (chips >= toCall) moves.push("call");
    if (
      state.raisesUsed < SHOW_ME_THE_COIN_RAISE_CAP &&
      chips >= toCall + SHOW_ME_THE_COIN_BET_UNIT
    ) {
      moves.push("raise");
    }
    return moves;
  }
  // No outstanding bet: check, or open with a bet.
  const moves: BetMove[] = ["check"];
  if (state.lastAggressorId === null && chips >= SHOW_ME_THE_COIN_BET_UNIT) {
    moves.push("bet");
  }
  return moves;
}

/** Round winner by submitted coin value, or "push" on a tie. */
export function compareSubmissions(
  state: ShowMeTheCoinState,
): ShowMeTheCoinPlayerId | "push" {
  const p1Value = coinValue(state.submission.p1?.coins ?? []);
  const p2Value = coinValue(state.submission.p2?.coins ?? []);
  if (p1Value > p2Value) return "p1";
  if (p2Value > p1Value) return "p2";
  return "push";
}

/** A player at 0 chips loses immediately. Returns the winner, else null. */
export function chipWinner(
  chips: Readonly<Record<ShowMeTheCoinPlayerId, number>>,
): ShowMeTheCoinPlayerId | null {
  if (chips.p1 <= 0 && chips.p2 <= 0) return null;
  if (chips.p1 <= 0) return "p2";
  if (chips.p2 <= 0) return "p1";
  return null;
}

/**
 * Symmetric hidden-info projection. A viewer sees their OWN inventory and full
 * submission; the opponent's inventory is hidden (null) and the opponent's
 * current submission is reduced to its count (denominations masked). A null
 * viewer (observer) gets both sides masked. `lastRound` (a settled round) is
 * public and not masked.
 */
export function filterStateForViewer(
  state: ShowMeTheCoinState,
  viewerPlayerId: string | null,
): ShowMeTheCoinViewerState {
  const projectInventory = (
    owner: ShowMeTheCoinPlayerId,
  ): CoinInventory | null =>
    viewerPlayerId === owner ? state.inventory[owner] : null;

  const projectSubmission = (owner: ShowMeTheCoinPlayerId) => {
    const submission = state.submission[owner];
    if (submission === null) return null;
    return {
      count: submission.coins.length,
      coins: viewerPlayerId === owner ? submission.coins : null,
    };
  };

  return {
    ...state,
    inventory: { p1: projectInventory("p1"), p2: projectInventory("p2") },
    submission: { p1: projectSubmission("p1"), p2: projectSubmission("p2") },
  };
}

export function buildViewerStates(
  state: ShowMeTheCoinState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, ShowMeTheCoinViewerState>> {
  const playerEntries = state.players.map(
    (player) =>
      [player.playerId, filterStateForViewer(state, player.playerId)] as const,
  );
  const observerEntries = [...new Set(observerUserIds)].map(
    (observerUserId) =>
      [observerUserId, filterStateForViewer(state, null)] as const,
  );
  return Object.fromEntries([...playerEntries, ...observerEntries]);
}
